import { Box, Dialog, DialogContent, DialogTitle, MenuItem, TextField, Typography } from '@freighthub/silhouette-core'
import { FC, ReactNode, useEffect, useState } from 'react'
import { useI18n } from 'utils/localisation'

import { DocumentInterface } from '../Documents'
import { DocumentType } from '../domain/Document'
import { useDocumentTypeState } from '../service/useDocumentTypes'
import { CAContainerNumberSelector } from './CAContainerNumberSelector'
import { DocumentsUpload, ProgressEventHandler } from './DocumentsUpload'

export type DocumentsUploadDialogProps = {
  open: boolean
  upload: (
    file: File,
    onProgress: ProgressEventHandler,
    fileType: string,
    options?: Record<string, string | string[]>
  ) => Promise<DocumentInterface>
  closeDialog: () => void
  handleError: (errorMessage: string) => void
  defaultValue?: string
  isTriangularShipment?: boolean
  allowedFormats?: string[]
}

export const DocumentsUploadDialog: FC<DocumentsUploadDialogProps> = ({
  defaultValue,
  open,
  closeDialog,
  handleError,
  isTriangularShipment,
  upload,
  allowedFormats,
}) => {
  const { translate } = useI18n()
  const [type, setType] = useState<string | null>(defaultValue || null)
  const [typeError, setTypeError] = useState<boolean>(false)
  const { documentTypes } = useDocumentTypeState()
  const [currentDocumentTypes, setCurrentDocumentTypes] = useState<DocumentType[]>([])
  const [selectedContainerNumbers, setSelectedContainerNumbers] = useState<string[]>([])

  const documentTypeMapper = (docType: DocumentType): ReactNode => {
    const translation = translate(docType.code)

    return (
      <MenuItem key={docType.code} value={docType.code} data-testid="document-type_menu_item">
        {translation === docType.code ? docType.displayName : translation}
      </MenuItem>
    )
  }

  const handleUpload = (file, onProgress: ProgressEventHandler) => {
    if (type) {
      return upload(file, onProgress, type, {
        containerNumbers: selectedContainerNumbers.length ? selectedContainerNumbers : [],
      })
    }
    setTypeError(true)
  }

  useEffect(() => {
    const getCurrentDocTypes = (): DocumentType[] =>
      documentTypes.filter((documentType) => {
        return documentType.selectors?.tags?.includes('ship')
      })

    if (documentTypes?.length) {
      setCurrentDocumentTypes(getCurrentDocTypes())
    }
  }, [documentTypes, open])

  return (
    <Dialog open={open} onClose={closeDialog} fullWidth data-testid="documents-upload_dialog_box">
      <DialogTitle onClose={closeDialog} showClose>
        {translate('frontend-sdk_upload_document')}
      </DialogTitle>
      {!!isTriangularShipment && (
        <Typography variant="subtitle1" color="error">
          Please pay attention: TRIANGULAR TRADE
        </Typography>
      )}
      <DialogContent>
        <TextField
          value={type}
          onChange={(e) => {
            setType(e.target.value)
          }}
          label={translate('frontend-sdk_select_file')}
          helperText={typeError ? translate('frontend-sdk_select_document_type_error') : null}
          error={typeError}
          margin="normal"
          data-e2e-id="select-upload-document_type"
          fullWidth
          select
          required
        >
          {currentDocumentTypes.map(documentTypeMapper)}
        </TextField>
        {type === 'customsAssessment' && (
          <Box mt={1}>
            <CAContainerNumberSelector
              onContainerNumbersChange={setSelectedContainerNumbers}
              selectedContainerNumbers={selectedContainerNumbers}
            />
          </Box>
        )}
      </DialogContent>

      {/* @ts-expect-error TS(2322) FIXME: Type '(file: any, onProgress: ProgressEventHandler... Remove this comment to see the full error message */}
      {!!type && <DocumentsUpload upload={handleUpload} handleError={handleError} allowedFormats={allowedFormats} />}
    </Dialog>
  )
}
